import {
  canAccessServiceModule,
  isAdmin,
  isServiceAdmin,
  type AccessClaims,
  type AccessProfile,
} from './accessControl';

interface RedirectLocationLike {
  pathname?: unknown;
  search?: unknown;
  hash?: unknown;
}

const toSafeString = (value: unknown): string => {
  return typeof value === 'string' ? value : '';
};

const isServiceRoleHint = (roleHint: unknown): boolean => {
  const normalized = String(roleHint || '')
    .trim()
    .toLowerCase();
  return normalized === 'serviceadmin' || normalized === 'serwisant';
};

const isGenericAppPath = (path: string): boolean => path === '/app' || path === '/app/';

export const resolveDefaultAppLanding = (
  profile?: AccessProfile | null,
  claims?: AccessClaims | null,
  roleHint?: unknown
): string => {
  if (isServiceRoleHint(roleHint)) {
    return '/app/service';
  }
  return canAccessServiceModule(profile, claims) ? '/app/service' : '/app';
};

export const resolveCommunityExitTarget = (
  profile?: AccessProfile | null,
  claims?: AccessClaims | null,
  currentPath = ''
): string => {
  if (String(currentPath || '').startsWith('/admin')) {
    return '/admin';
  }
  if (isAdmin(profile, claims)) {
    return '/admin';
  }
  if (isServiceAdmin(profile, claims)) {
    return '/app/service';
  }
  return '/app/discover';
};

export const resolvePostLoginRedirect = (from: unknown, fallback = '/app'): string => {
  const candidate = (from || {}) as RedirectLocationLike;
  const pathname = toSafeString(candidate.pathname);
  const search = toSafeString(candidate.search);
  const hash = toSafeString(candidate.hash);

  const targetPath = pathname ? `${pathname}${search}${hash}` : fallback;
  if (
    targetPath.startsWith('/login') ||
    targetPath.startsWith('/register') ||
    targetPath.startsWith('/verify-email')
  ) {
    return fallback;
  }

  return targetPath;
};

export const resolvePostAuthRedirect = (options: {
  from: unknown;
  intentPath?: string | null;
  fallback: string;
}): string => {
  const fromPath = toLocationPath(options.from);
  const shouldPreferIntent =
    Boolean(options.intentPath) && (!fromPath || isGenericAppPath(fromPath));
  const preferredFrom = shouldPreferIntent ? { pathname: options.intentPath } : options.from;
  return resolvePostLoginRedirect(preferredFrom, options.fallback);
};

export const toLocationPath = (value: unknown): string | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const candidate = value as RedirectLocationLike;
  const pathname = toSafeString(candidate.pathname);
  const search = toSafeString(candidate.search);
  const hash = toSafeString(candidate.hash);
  if (!pathname) {
    return null;
  }
  return `${pathname}${search}${hash}`;
};
